import {useNavigate, useParams} from "react-router-dom";
import {
    useCreateCannedNoteMutation,
    useDeleteCannedNoteMutation,
    useGetCannedNoteByIdQuery,
    useUpdateCannedNoteMutation
} from "./cannedNotesApi";
import React, {ChangeEvent, FC, FormEvent, useEffect, useState} from "react";
import Breadcrumb, {Crumb} from "../../../components/common/Breadcrumbs";
import Form from "../../../components/common/form/Form";
import InputField from "../../../components/common/form/InputField";
import TextAreaField from "../../../components/common/form/TextAreaField";
import {useGetEnumsQuery} from "../../../app/globalApi";
import {createOptionListForSelectTag} from "../../../helpers/utils/Utils";
import SelectField from "../../../components/common/form/SelectField";
import {getEnumDisplayNameByConstant} from "../../../helpers/utils/EnumUtils";
import {handleRTKQuery} from "../../../helpers/utils/RtkQueryUtils";
import {Routes as AppRoutes} from "../../../routes";
import AuditableInfoButton from "../../../components/common/form/AuditableInfoButton";
import {hasPermission} from "../../../helpers/utils/AccessControlUtils";
import ButtonTray from "../../../components/common/form/ButtonTray";
import ModalButton from "../../../components/common/form/ModalButton";
import IconButton from "../../../components/common/form/IconButton";
import {FaTrash} from "react-icons/fa";
import ConfirmationModal from "../../../components/layout/modal/ConfirmationModal";

interface Props {
    crumbs: Crumb[];
    title: string;
    btnText: string;
    isEditable?: boolean;
    isReadOnly?: boolean
}

const CannedNotesFrom: FC<Props> = ({crumbs, title, btnText, isEditable = true, isReadOnly = false}) => {

    const navigate = useNavigate();
    let {noteId} = useParams();

    const cannedNoteApi = useGetCannedNoteByIdQuery(noteId);
    const [cannedNoteData, setCannedNoteData] = useState<any>({});
    const [updateCannedNoteApi, updateCannedNoteApiResponse] = useUpdateCannedNoteMutation()
    const [createCannedNoteApi, createCannedNoteApiResponse] = useCreateCannedNoteMutation()
    const {data: enums} = useGetEnumsQuery("");
    const [deleteCannedNotes, {isLoading}] = useDeleteCannedNoteMutation();

    useEffect(() => {
        document.title = "Canned Notes"
    });
    useEffect(() => {
        let obj = cannedNoteApi?.data;
        if (obj) {
            setCannedNoteData({
                ...obj,
                type: obj.type ? {
                    value: obj.type,
                    label: getEnumDisplayNameByConstant(
                        enums?.["CertificateNoteType"],
                        obj.type
                    ),
                } : null,
            });
        }
    }, [cannedNoteApi?.data,enums]);

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        let obj = {
            ...cannedNoteData,
            type: cannedNoteData.type?.value,
        }

        const CreateOrUpdateSystemProperty = async () => {

            if (btnText === "Create") {
                return await createCannedNoteApi(obj)
            } else {
                return await updateCannedNoteApi({
                    id: noteId,
                    requestBody: obj,
                }).unwrap();
            }
        }

        await handleRTKQuery(CreateOrUpdateSystemProperty, (response: any) => {
            if (response?.success || response?.data?.success) {
                setCannedNoteData({});
                navigate(AppRoutes.CannedNotes.path);
            }
        });

    }


    const handleOnChange = (e: ChangeEvent<HTMLInputElement> | {
        name: string;
        value: any
    } | ChangeEvent<HTMLTextAreaElement>) => {
        const {name, value} = "target" in e ? e.target : e;
        setCannedNoteData({
            ...cannedNoteData,
            [name]: value,
        })
    }


    const handleDelete = async () => {
        if (noteId) {
            await deleteCannedNotes(noteId).unwrap();
        }
        navigate(AppRoutes.CannedNotes.path)
    };
    const handleToggle = (property: string) => () => {
        setCannedNoteData((prevData: any) => ({
            ...prevData,
            [property]: !prevData[property]
        }));
    };


    return (
        <>
            <div className="px-4 py-2">

                <div className={"justify-between sm:block md:flex lg:flex"}>
                    <Breadcrumb crumbs={crumbs}/>
                    <div className={"justify-between sm:block md:flex lg:flex"}>
                        {noteId && (
                            <>
                                {hasPermission(["CANNOTE-D"]) && <ModalButton
                                    triggerButton={
                                        <IconButton
                                            icon={<FaTrash/>}
                                            title={"Delete Auto Hoa"}
                                            hasBackground={true}
                                            className="mr-2"
                                        />
                                    }
                                    modal={
                                        <ConfirmationModal
                                            show={true}
                                            setShow={() => {
                                            }}
                                            title={"Delete Canned Note"}
                                            bodyMessage={"Are you sure you want to delete this Canned Note?"}
                                            onSubmit={handleDelete}
                                            isLoading={isLoading}
                                        />
                                    }
                                />}

                                {
                                    hasPermission(["AUDIT-V"]) &&
                                    < AuditableInfoButton obj={cannedNoteApi?.data}/>}
                            </>
                        )}


                    </div>
                </div>
                <div className="flex justify-center mt-3">
                    <div className="bg-text-0 lg:w-4/6 sm:w-full p-5 rounded-md shadow-[0_0_10px_-2px_rgba(0,0,0,0.2)]">
                        <h3 className="text-xl font-semibold text-surface-0 pb-3 border-b border-surface-7">{title}</h3>
                        <Form onSubmit={handleSubmit} fields={<>
                            <div className="grid grid-cols-12">
                                {btnText !== "Create" && <InputField
                                    name={"noteNumber"}
                                    label="Note No."
                                    onChange={handleOnChange}
                                    value={cannedNoteData?.noteNumber || ""}
                                    type="number"
                                    placeholder="Enter Note No...."
                                    required
                                    disabled={true}
                                />}
                                <TextAreaField
                                    label={"Comment"}
                                    placeholder={"Enter Comment...."}
                                    onChange={handleOnChange}
                                    required={true}
                                    name={"comment"}
                                    value={cannedNoteData?.comment || ""}
                                    disabled={isReadOnly}
                                />

                                <SelectField
                                    placeholder='Select CertificateNote Type...'
                                    value={cannedNoteData?.type || null}
                                    options={createOptionListForSelectTag(enums?.["CertificateNoteType"], "value", "constant")}
                                    onChange={(selectedOption) => {
                                        handleOnChange({name: "type", value: selectedOption})
                                    }}
                                    required={true}
                                    label={"CertificateNote Type"}
                                    isDisabled={isReadOnly}
                                />

                                <InputField
                                    name="bold"
                                    label="Bold"
                                    onChange={handleToggle("bold")}
                                    checked={cannedNoteData.bold || ""}
                                    type="checkbox"
                                    disabled={isReadOnly}
                                />

                                <InputField
                                    name="yellowHighlight"
                                    label="Yellow Highlight"
                                    onChange={handleToggle("yellowHighlight")}
                                    checked={cannedNoteData.yellowHighlight || ""}
                                    type="checkbox"
                                    disabled={isReadOnly}
                                />

                                <InputField
                                    name="redColor"
                                    label="Red Color"
                                    onChange={handleToggle("redColor")}
                                    checked={cannedNoteData.redColor || ""}
                                    type="checkbox"
                                    disabled={isReadOnly}
                                />

                            </div>
                            {!isReadOnly &&

                                <div className="col-span-12 md:grid md:grid-cols-12 gap-3">
                                    <div className="col-span-3 sm:mt-2 lg:mt-0 mt-2 flex items-center">
                                    </div>
                                    <div className="relative col-span-9">
                                        <div className={"mt-4 flex"}>
                                            <ButtonTray
                                                buttons={[
                                                    {
                                                        buttonProps: {
                                                            btnText: "Cancel",
                                                            onClick: () => navigate(AppRoutes.CannedNotes.path),
                                                            type: "cancel",
                                                        },
                                                        buttonType: "button"
                                                    },
                                                    {
                                                        buttonProps: {
                                                            btnText: "Submit",
                                                            type: "submit",
                                                            isLoading: (updateCannedNoteApiResponse.isLoading || createCannedNoteApiResponse.isLoading),

                                                        },
                                                        buttonType: "button",

                                                    }
                                                ]}
                                                align="center"
                                                gap={2}
                                            />
                                        </div>
                                    </div>
                                </div>

                            }
                        </>}/>
                    </div>
                </div>
            </div>
        </>
    )
}

export default CannedNotesFrom;